import React, { useMemo, useState } from "react";
import {
  Image,
} from 'antd';
import Logo from '../assets/logo-saha.svg';

type SubjectKey = "turkce" | "inkilap" | "din" | "yabanci" | "matematik" | "fen";

type FormValue = {
  dogru: string | number;
  yanlis: string | number;
};

type Subject = {
  key: SubjectKey;
  name: string;
  q: number;
  coeffKey: SubjectKey;
};

// 2024 yaklaşıklık katsayıları (net başına puan etkisi)
const COEFFS_2024: Record<SubjectKey, number> = {
  turkce: 4.348,
  inkilap: 1.666,
  din: 1.899,
  yabanci: 1.5075,
  matematik: 4.2538,
  fen: 4.123,
};

// 2024 için sabit (yaklaşık)
const BASE_2024 = 194.752082;

const SUBJECTS: Subject[] = [
  { key: "turkce", name: "Türkçe", q: 20, coeffKey: "turkce" },
  { key: "inkilap", name: "T.C. İnkılap Tarihi ve Atatürkçülük", q: 10, coeffKey: "inkilap" },
  { key: "din", name: "Din Kültürü ve Ahlak Bilgisi", q: 10, coeffKey: "din" },
  { key: "yabanci", name: "Yabancı Dil", q: 10, coeffKey: "yabanci" },
  { key: "matematik", name: "Matematik", q: 20, coeffKey: "matematik" },
  { key: "fen", name: "Fen Bilimleri", q: 20, coeffKey: "fen" },
];

export default function LGSPuanHesapla() {
  const [form, setForm] = useState<Record<SubjectKey, FormValue>>(
    SUBJECTS.reduce((acc, s) => {
      acc[s.key] = { dogru: "", yanlis: "" };
      return acc;
    }, {} as Record<SubjectKey, FormValue>)
  );

  const [score, setScore] = useState<number | null>(null);

  const onChange = (key: SubjectKey, field: keyof FormValue, value: string) => {
    let v = value.replace(",", ".");
    if (v === "") {
      setForm((f) => ({ ...f, [key]: { ...f[key], [field]: "" } }));
      return;
    }
    const num = Math.max(0, Number(v));
    if (!Number.isFinite(num)) return;

    setForm((f) => {
      const s = SUBJECTS.find((x) => x.key === key);
      if (!s) return f;
      let dogru = field === "dogru" ? num : Number(f[key].dogru) || 0;
      let yanlis = field === "yanlis" ? num : Number(f[key].yanlis) || 0;

      // Mantık: doğru ≤ soru, (doğru + yanlış) ≤ soru
      dogru = Math.min(dogru, s.q);
      yanlis = Math.min(yanlis, s.q - dogru);

      return { ...f, [key]: { dogru, yanlis } };
    });
  };

  const rows = useMemo(() => {
    return SUBJECTS.map((s) => {
      const d = Number(form[s.key].dogru) || 0;
      const y = Number(form[s.key].yanlis) || 0;
      const net = d - y / 3; // MEB tanımı
      return {
        ...s,
        dogru: d,
        yanlis: y,
        net,
      };
    });
  }, [form]);

  const totals = useMemo(() => {
    const soru = SUBJECTS.reduce((t, s) => t + s.q, 0);
    const dogru = rows.reduce((t, r) => t + r.dogru, 0);
    const yanlis = rows.reduce((t, r) => t + r.yanlis, 0);
    const net = rows.reduce((t, r) => t + r.net, 0);
    return { soru, dogru, yanlis, net };
  }, [rows]);

  const formatNum = (n: number, frac = 2) =>
    Number.isFinite(n)
      ? n.toLocaleString("tr-TR", {
        minimumFractionDigits: frac,
        maximumFractionDigits: frac,
      })
      : "-";

  const hesapla = () => {
    const scoreApprox =
      BASE_2024 +
      rows.reduce(
        (sum, r) => sum + (COEFFS_2024[r.coeffKey] || 0) * (r.net || 0),
        0
      );

    setScore(
      Number.isFinite(scoreApprox) ? Math.max(100, Math.min(500, scoreApprox)) : null
    );
  };

  const temizle = () => {
    setForm(
      SUBJECTS.reduce((acc, s) => {
        acc[s.key] = { dogru: "", yanlis: "" };
        return acc;
      }, {} as Record<SubjectKey, FormValue>)
    );
    setScore(null);
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
                <div style={{ color: "#a3aed8", textAlign: "center", marginBottom: 16 }}>
          <Image width={200} preview={false} src={Logo}></Image>
        </div>
        <h2 style={styles.title}>LGS Puan Hesaplama</h2>

        <div style={styles.panel}>
          <table style={{ ...styles.table, width: '100%', borderCollapse: 'collapse' }}>
            <thead style={styles.head as React.CSSProperties}>
              <tr>
                <th style={{ ...styles.cell, textAlign: 'left', fontWeight: 700 }}>Ders Adı</th>
                <th style={styles.cell}>Soru</th>
                <th style={styles.cell}>Doğru</th>
                <th style={styles.cell}>Yanlış</th>
                <th style={styles.cell}>Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td style={{ ...styles.cell, textAlign: 'left' }}><b>{r.name}</b></td>
                  <td style={styles.cell}>{r.q}</td>
                  <td style={styles.cell}>
                    <input
                      inputMode="numeric"
                      pattern="[0-9]*"
                      type="number"
                      min={0}
                      max={r.q}
                      step={1}
                      value={form[r.key].dogru}
                      onChange={(e) => onChange(r.key, 'dogru', e.target.value)}
                      style={styles.input}
                      aria-label="doğru sayısı"
                    />
                  </td>
                  <td style={styles.cell}>
                    <input
                      inputMode="numeric"
                      pattern="[0-9]*"
                      type="number"
                      min={0}
                      max={r.q}
                      step={1}
                      value={form[r.key].yanlis}
                      onChange={(e) => onChange(r.key, 'yanlis', e.target.value)}
                      style={styles.input}
                      aria-label="yanlış sayısı"
                    />
                  </td>
                  <td style={styles.cell}><strong>{formatNum(r.net)}</strong></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#fff7ef' }}>
                <td style={{ ...styles.cell, textAlign: 'left', fontWeight: 700 }}>Toplam</td>
                <td style={{ ...styles.cell, fontWeight: 700 }}>{totals.soru}</td>
                <td style={{ ...styles.cell, fontWeight: 700 }}>{totals.dogru}</td>
                <td style={{ ...styles.cell, fontWeight: 700 }}>{totals.yanlis}</td>
                <td style={{ ...styles.cell, fontWeight: 700 }}>
                  <strong>{formatNum(totals.net)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>

          <div style={styles.actions}>
            <button onClick={hesapla} style={{ ...styles.btn, ...styles.btnPrimary }}>
              Hesapla
            </button>
            <button onClick={temizle} style={{ ...styles.btn, ...styles.btnGhost }}>
              Temizle
            </button>
          </div>
        </div>

        <div style={styles.resultWrap}>
          <div style={styles.resultBox}>
            <div style={styles.resultLabel}>LGS Puanı</div>
            <div style={styles.resultValue}>
              {score == null
                ? '-'
                : score.toLocaleString('tr-TR', {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f6f7fb",
    padding: 24,
    boxSizing: "border-box",
    fontFamily:
      "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
  },
  card: {
    width: "min(1200px, 100%)",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(0,0,0,.06)",
    padding: 24,
  },
  title: {
    fontSize: 22,
    display: "block",
    fontWeight: 700,
        textAlign: "center",
    color: "rgb(114, 63, 231)"
  },
  table: {
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #eee",
  },
  panel: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #eee',
    padding: 16,
  },
  row: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #eee",
  },
  head: {
    background: "rgb(114, 63, 231)",
    color: "#fff",
    fontWeight: 700,
  },
  footer: {
    background: "#fff7ef",
    fontWeight: 700,
  },
  cell: {
    flex: 1,
    padding: "12px 10px",
    fontSize: 14,
    textAlign: "center",
  },
  input: {
    width: "84px",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #ddd",
    fontSize: 14,
    textAlign: "center",
    outline: "none",
  },
  actions: {
    marginTop: 16,
    display: "flex",
    gap: 12,
  },
  btn: {
    padding: "10px 16px",
    borderRadius: 10,
    border: "1px solid transparent",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  btnPrimary: {
    background: "rgb(114, 63, 231)",
    color: "#fff",
  },
  btnGhost: {
    background: "#fff",
    color: "#222",
    border: "1px solid #ddd",
  },
  resultWrap: {
    marginTop: 20,
    display: "flex",
    justifyContent: "flex-start",
    gap: 12,
  },
  resultBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: 20,
    background: "#f4f7ff",
    border: "1px solid #e2e9ff",
    borderRadius: 12,
    padding: "14px 16px",
  },
  resultLabel: { fontWeight: 700, fontSize: 14, color: "#3a4a7a" },
  resultValue: { fontWeight: 800, fontSize: 20 },
  note: { marginTop: 14, color: "#444", fontSize: 13 },
};
